import { useMemo } from 'react'
import { ObjectiveGradingScale } from 'react-common'
import { useFilterCoursesByCollection } from '@/hooks/useFilterCoursesByCollection'
import { useQueryGradingScalesQuery, useQueryObjectiveAccessesQuery } from '@/store/api/aggregatesApi'
import { COLLECTION_NO_VALUE } from '@/store/api/showcasesApi'
import { useAppSelector } from '@/store/hooks'
import { createFiltersSelector } from '@/store/slices/aggregatesSlice'
import { useGetEnrolledCoursesFilter } from './useGetEnrolledCoursesFilter'

export const useQueryEnrolledCourses = () => {
  const coursesFilter = useGetEnrolledCoursesFilter()
  const collectionFilters = useAppSelector((state) => createFiltersSelector(state, 'enrolled').collection)

  const {
    data: { data: enrolledCourses = [], totalCount: totalEnrolledCourses = 0 } = {},
    isLoading: isEnrolledCoursesLoading,
  } = useQueryObjectiveAccessesQuery(
    { query: { filter: coursesFilter } },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const enrolledCoursesGradingScaleIds = [
    ...new Set((enrolledCourses || []).map((oa) => oa.objective?.gradingScaleId).filter(Boolean)),
  ]

  const { data: gradingScalesResponse } = useQueryGradingScalesQuery(
    {
      query: {
        filter: `[${enrolledCoursesGradingScaleIds.map((id) => `["id", "=", "${id}"]`).join(', "or", ')}]`,
      },
    },
    {
      skip: !enrolledCoursesGradingScaleIds.length,
      refetchOnMountOrArgChange: true,
    },
  )

  const filteredEnrolledCourses = useFilterCoursesByCollection({ type: 'enrolled', courses: enrolledCourses })
  const uncategorizedCoursesCount = enrolledCourses.filter((course) => !course.objective.showcases?.length).length
  const hasNoShowCase =
    collectionFilters.includes(COLLECTION_NO_VALUE) ||
    (uncategorizedCoursesCount > 0 && uncategorizedCoursesCount !== enrolledCourses.length)

  const gradingScaleMapped = useMemo(() => {
    return filteredEnrolledCourses.map((oa) => {
      const cloned = structuredClone(gradingScalesResponse)
      const foundGradingScale = (cloned?.data || []).find((gs) => gs.id === oa.objective.gradingScaleId)

      // FE always expects an object, even if it's empty
      // otherwise getScoreColor became broken
      let objectiveGradingScale: ObjectiveGradingScale | null = {
        id: '00000000-0000-0000-0000-000000000000',
        maxScore: 0,
        name: '',
        description: '',
        normalizedPassingScore: 0,
        numberOfGrades: 0,
        // @ts-expect-error it should be null
        objectiveGrades: null,
        passingGradeId: '00000000-0000-0000-0000-000000000000',
      }

      if (foundGradingScale) {
        const { grades, ...rest } = foundGradingScale
        objectiveGradingScale = {
          ...rest,
          objectiveGrades: grades,
          numberOfGrades: 1, // INFO: not important and used property
          normalizedPassingScore: 1, // INFO: not important and used property(i hope)
        }
      }

      return {
        ...oa,
        objectiveWorkflowAggregate: {
          ...oa.objectiveWorkflowAggregate,
          objectiveGradingScale,
        },
      }
    })
  }, [filteredEnrolledCourses, gradingScalesResponse])

  return {
    enrolledCourses: gradingScaleMapped,
    totalEnrolledCourses,
    isEnrolledCoursesLoading,
    hasNoShowCase,
  }
}
